export const IPO_REGISTRATION_FORMS = new Set(['S-1', 'S-1/A', 'F-1', 'F-1/A']);

export const isIpoRegistrationForm = (form) => (
  IPO_REGISTRATION_FORMS.has(String(form || '').toUpperCase())
);

export const getIpoPeriodLabel = (form, fallbackLabel = null) => {
  if (fallbackLabel) return fallbackLabel;
  const normalizedForm = String(form || '').toUpperCase();
  if (normalizedForm === 'S-1/A') return 'IPO S-1/A';
  if (normalizedForm === 'F-1') return 'IPO F-1';
  if (normalizedForm === 'F-1/A') return 'IPO F-1/A';
  if (normalizedForm === 'S-1') return 'IPO S-1';
  if (isIpoRegistrationForm(normalizedForm)) return `IPO ${normalizedForm}`;
  return 'IPO S-1';
};

export const isIpoChartPeriod = (item) => {
  if (!item) return false;
  if (item.isIpoPeriod === true) return true;
  return isIpoRegistrationForm(item.sourceForm);
};

export const getPreliminary8kLabel = (fallbackLabel = null) => (
  fallbackLabel || 'Preliminary 8-K'
);

export const isPreliminary8kChartPeriod = (item) => {
  if (!item) return false;
  // Explicit false from API means this period is already authoritative (10-Q/10-K).
  if (item.isPreliminary === false) return false;
  if (item.isPreliminary === true) return true;
  const form = String(item.sourceForm || '').toUpperCase();
  // Never treat 10-Q/10-K/IPO registration points as preliminary.
  if (
    form.startsWith('10-Q')
    || form.startsWith('10-K')
    || form.startsWith('20-F')
    || form.startsWith('40-F')
    || form.startsWith('6-K')
    || isIpoRegistrationForm(form)
  ) {
    return false;
  }
  const origin = String(item.sourceOrigin || '').toLowerCase();
  if (origin.includes('8k') && (origin.includes('prelim') || origin.includes('earnings'))) {
    return true;
  }
  const source = String(item.source || '').toLowerCase().replace(/-/g, '_');
  if (source.includes('8k_prelim') || source === '8k_preliminary') {
    return true;
  }
  // Bare 8-K sourceForm on a chart row is a preliminary stand-in.
  if (form.startsWith('8-K')) {
    return true;
  }
  return false;
};

export const excludePreliminary8kRows = (rows) => (
  (Array.isArray(rows) ? rows : []).filter((row) => !isPreliminary8kChartPeriod(row))
);

/**
 * Preliminary 8-K is only valid on the single latest series point.
 * Older mid-series prelim flags (or bare 8-K forms) are cleared so charts
 * never show a cyan standout in the middle of history.
 */
export const retainOnlyLatestPreliminary8k = (rows) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  if (sourceRows.length === 0) return sourceRows;

  const sortedAsc = [...sourceRows].sort(
    (a, b) => new Date(a?.period || 0).getTime() - new Date(b?.period || 0).getTime()
  );
  const latestIdx = sortedAsc.length - 1;

  return sortedAsc.map((row, idx) => {
    const looksPrelim = isPreliminary8kChartPeriod(row);
    if (!looksPrelim) {
      return {
        ...row,
        isPreliminary: false,
        preliminaryLabel: null,
      };
    }
    if (idx !== latestIdx) {
      // Historical prelim stand-in is invalid once later points exist.
      return {
        ...row,
        isPreliminary: false,
        preliminaryLabel: null,
        // Keep values, but drop 8-K standout form so UI does not re-detect it.
        sourceForm: (
          String(row?.sourceForm || '').toUpperCase().startsWith('8-K')
            ? null
            : row?.sourceForm
        ),
        sourceOrigin: (
          String(row?.sourceOrigin || '').toLowerCase().includes('8k')
            ? 'database'
            : row?.sourceOrigin
        ),
        source: (
          String(row?.source || '').toLowerCase().includes('8k')
            ? null
            : row?.source
        ),
      };
    }
    return {
      ...row,
      isPreliminary: true,
      preliminaryLabel: getPreliminary8kLabel(row?.preliminaryLabel),
      sourceForm: row?.sourceForm || '8-K',
    };
  });
};

export const normalizeRevenueOperatingSeries = (rows, lookbackYears = 10) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const now = new Date();
  const minDate = new Date(now);
  minDate.setFullYear(now.getFullYear() - lookbackYears);

  const filtered = sourceRows
    .filter((row) => {
      const periodDate = new Date(row?.period);
      if (!Number.isFinite(periodDate.getTime()) || periodDate < minDate) {
        return false;
      }

      const revenue = Number(row?.Revenue);
      return Number.isFinite(revenue) && revenue > 0;
    })
    .map((row) => {
      let next = row;
      const ipo = isIpoChartPeriod(row);
      if (ipo) {
        next = {
          ...next,
          isIpoPeriod: true,
          ipoLabel: getIpoPeriodLabel(row?.sourceForm, row?.ipoLabel),
        };
      }
      return next;
    })
    .sort((a, b) => new Date(a.period) - new Date(b.period));

  return excludePreliminary8kRows(filtered);
};

const parseNumericValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(/,/g, '').trim();
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const getRevenueValue = (row) => {
  const candidates = [row?.Revenue, row?.revenue, row?.['Total Revenue']];
  for (const candidate of candidates) {
    const parsed = parseNumericValue(candidate);
    if (parsed !== null) return parsed;
  }
  return null;
};

export const getOperatingIncomeValue = (row) => {
  const candidates = [
    row?.['Operating Income'],
    row?.operating_income,
    row?.operatingIncome,
    row?.['Operating Income/Loss'],
    row?.['Operating Loss'],
  ];

  for (const candidate of candidates) {
    const parsed = parseNumericValue(candidate);
    if (parsed !== null) return parsed;
  }

  return null;
};

export const getNetIncomeValue = (row) => {
  const signedCandidates = [
    row?.['Net Income'],
    row?.net_income,
    row?.netIncome,
    row?.['Net Income/Loss'],
  ];

  for (const candidate of signedCandidates) {
    const parsed = parseNumericValue(candidate);
    if (parsed !== null) return parsed;
  }

  const lossCandidates = [
    row?.['Net Loss'],
    row?.net_loss,
    row?.netLoss,
  ];

  for (const candidate of lossCandidates) {
    const parsed = parseNumericValue(candidate);
    if (parsed !== null) return -Math.abs(parsed);
  }

  return null;
};
